export const API_URL = '/api';
import { getLocalDateString } from './utils';

// Types
export interface Staff {
  id: string;
  name: string;
  email: string;
  password?: string;
  department: string;
  position: string;
  avatar?: string;
  faceDescriptor?: number[];
  faceImage?: string;
  walletBalance?: number;
  totalWages?: number;
  offDays?: string[] | null;
  isSpecial?: boolean;
  upiId?: string;
  travelsByBike?: boolean;
  bikeRatePerKm?: number;
  entryTime?: string;
  exitTime?: string;
  createdAt: string;
}

export interface Expense {
  id: string;
  staffId: string;
  staffName?: string;
  date: string;
  amount: number;
  paidAmount?: number;
  category?: string;
  clientName?: string;
  remark?: string;
  status: 'pending' | 'approved' | 'rejected';
  adminRemark?: string;
  isGst?: boolean;
  gstAmount?: number;
  withoutGstAmount?: number;
  bank?: string;
  paymentMethod?: string;
  rfNo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaidLeave {
  id: string;
  staffId: string;
  staffName?: string;
  date: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  adminRemark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Wallet {
  staffId: string;
  name: string;
  balance: number;
}

export interface StatusUpdate {
  id: string;
  taskId: string;
  previousStatus: 'Pending' | 'In Progress' | 'Completed';
  newStatus: 'Pending' | 'In Progress' | 'Completed';
  updatedBy: string;
  updatedByName: string;
  updatedAt: string;
}

export interface PlatformData {
  name: string;
  startDate: string;
  endDate: string;
  amount: number;
  times?: string[];
  status: 'Pending' | 'In Progress' | 'Completed';
}

export interface Task {
  id: string;
  title: string;
  description: string;
  clientName?: string;
  clientWap?: string;
  campaignName?: string;
  year?: string;
  location?: string;
  platforms?: PlatformData[];
  remarks?: string;
  assignedStaff: string[]; // Multiple staff IDs
  assignedTo?: string; // Kept for backward compatibility if needed, but we'll use assignedStaff
  createdBy: string;
  createdByName: string;
  priority: 'P0' | 'P1' | 'P2';
  status: 'Pending' | 'In Progress' | 'Completed';
  deadline: string;
  createdAt: string;
  updatedAt: string;
  comments: TaskComment[];
  statusHistory: StatusUpdate[];
  files?: string[];
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

export interface AttendanceRecord {
  id: string;
  staffId: string;
  staffName?: string;
  date: string;
  checkIn?: string;
  checkOut?: string;
  clockIn?: string;
  clockOut?: string;
  status: 'present' | 'absent' | 'late';
  workingHours?: number;
  latitude?: number;
  longitude?: number;
  verification?: 'verified' | 'pending' | 'approved' | 'rejected';
  kmTraveled?: number;
  travelExpenses?: number;
}

export interface AttendanceStats {
  lateOverTime: { date: string; late: number; lateMinutes: number }[];
  statusBreakdown: { present: number; late: number; absent: number };
  workingHoursTrend: { date: string; avgHours: number }[];
  staffSummary: { staffId: string; name: string; present: number; late: number; absent: number; lateMinutes: number; totalHours: number; avgHours: number }[];
  checkInDistribution: { hour: number; count: number }[];
  staffDetail: {
    staffId: string;
    name: string;
    totalPresent: number;
    totalLate: number;
    totalAbsent: number;
    totalLateMinutes: number;
    totalLateHours: number;
    totalWorkingHours: number;
    avgWorkingHours: number;
    overtimeHours: number;
    totalExtraHours?: number;
    lateDetails: { date: string; checkIn: string; lateMinutes: number }[];
    walletBalance: number;
    totalEarned: number;
    pendingExpenses: number;
    totalEarnings: number;
    totalWages: number;
    offDays: string[];
    daysInMonth: number;
    offDaysInMonth: number;
    workingDaysInMonth: number;
    daysWorked: number;
    daysPaid: number;
    expectedMonthlySalary: number;
    earnedSalary: number;
    salaryDeduction: number;
    currentMonth: string;
    lateOverTime: { date: string; late: number; lateMinutes: number }[];
    workingHoursTrend: { date: string; avgHours: number }[];
    checkInDistribution: { hour: number; count: number }[];
  } | null;
}

export interface DailyReport {
  id: string;
  staffId: string;
  staffName: string;
  date: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  userId: string;
  role: 'admin' | 'staff';
  name: string;
  email: string;
}

// Helper for API calls
const api = {
  async get<T>(endpoint: string): Promise<T> {
    const res = await fetch(`${API_URL}${endpoint}`);
    if (!res.ok) throw new Error(`API Error: ${res.statusText}`);
    return res.json();
  },

  async post<T>(endpoint: string, data: any): Promise<T> {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`API Error: ${res.statusText}`);
    return res.json();
  },

  async put<T>(endpoint: string, data: any): Promise<T> {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`API Error: ${res.statusText}`);
    return res.json();
  },

  async delete<T = void>(endpoint: string): Promise<T> {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`API Error: ${res.statusText}`);
    // Return JSON if response has content, otherwise return void
    const text = await res.text();
    return text ? JSON.parse(text) : undefined as T;
  }
};

// Staff operations
export const staffService = {
  async getAll(): Promise<Staff[]> {
    return api.get<Staff[]>('/staff');
  },

  async getById(id: string): Promise<Staff | undefined> {
    const staff = await this.getAll();
    return staff.find(s => s.id === id);
  },

  async create(staff: Omit<Staff, 'id' | 'createdAt'>): Promise<Staff> {
    return api.post<Staff>('/staff', staff);
  },

  async update(id: string, updates: Partial<Staff>): Promise<Staff> {
    return api.put<Staff>(`/staff/${id}`, updates);
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/staff/${id}`);
  },
};

// Task operations
export const taskService = {
  async getAll(): Promise<Task[]> {
    return api.get<Task[]>('/tasks');
  },

  async getByStaffId(staffId: string): Promise<Task[]> {
    const tasks = await this.getAll();
    return tasks.filter(t => t.assignedTo === staffId);
  },

  async getById(id: string): Promise<Task | undefined> {
    const tasks = await this.getAll();
    return tasks.find(t => t.id === id);
  },

  async create(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'comments' | 'statusHistory'>): Promise<Task> {
    return api.post<Task>('/tasks', task);
  },

  async update(id: string, updates: Partial<Task>): Promise<Task> {
    return api.put<Task>(`/tasks/${id}`, updates);
  },

  async updateStatus(taskId: string, newStatus: 'Pending' | 'In Progress' | 'Completed', updatedBy: string, updatedByName: string): Promise<Task | null> {
    const task = await this.getById(taskId);
    if (!task) return null;

    const statusUpdate: StatusUpdate = {
      id: crypto.randomUUID(),
      taskId,
      previousStatus: task.status,
      newStatus,
      updatedBy,
      updatedByName,
      updatedAt: new Date().toISOString(),
    };

    return this.update(taskId, {
      status: newStatus,
      statusHistory: [...(task.statusHistory || []), statusUpdate],
    });
  },

  async addComment(taskId: string, comment: Omit<TaskComment, 'id' | 'createdAt'>): Promise<TaskComment | null> {
    const task = await this.getById(taskId);
    if (!task) return null;

    const newComment: TaskComment = {
      ...comment,
      id: crypto.randomUUID(),
      taskId,
      createdAt: new Date().toISOString(),
    };

    await this.update(taskId, {
      comments: [...task.comments, newComment]
    });
    return newComment;
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/tasks/${id}`);
  },
};

// Attendance operations
export const attendanceService = {
  async getAll(): Promise<AttendanceRecord[]> {
    return api.get<AttendanceRecord[]>('/attendance');
  },

  async getByStaffId(staffId: string): Promise<AttendanceRecord[]> {
    const records = await this.getAll();
    return records.filter(a => a.staffId === staffId);
  },

  async getByDate(date: string): Promise<AttendanceRecord[]> {
    const records = await this.getAll();
    return records.filter(a => a.date === date);
  },

  async getTodayForStaff(staffId: string): Promise<AttendanceRecord | undefined> {
    const today = getLocalDateString();
    const records = await this.getAll();
    return records.find(a => a.staffId === staffId && a.date === today);
  },

  async clockIn(staffId: string): Promise<AttendanceRecord> {
    const today = getLocalDateString();
    const now = new Date().toISOString();
    const hour = new Date().getHours();
    const status: 'present' | 'late' = hour >= 9 ? 'late' : 'present';

    const record = {
      staffId,
      date: today,
      clockIn: now,
      status,
    };
    return api.post<AttendanceRecord>('/attendance', record);
  },

  async clockOut(recordId: string): Promise<AttendanceRecord | null> {
    const records = await this.getAll();
    const record = records.find(r => r.id === recordId);
    if (!record) return null;

    const now = new Date();
    const clockIn = new Date(record.clockIn!);
    const workingHours = (now.getTime() - clockIn.getTime()) / (1000 * 60 * 60);

    return api.put<AttendanceRecord>(`/attendance/${recordId}`, {
      clockOut: now.toISOString(),
      workingHours: Math.round(workingHours * 100) / 100,
    });
  },

  async update(recordId: string, updates: Partial<AttendanceRecord>): Promise<AttendanceRecord> {
    return api.put<AttendanceRecord>(`/attendance/${recordId}`, updates);
  },

  async updateKm(recordId: string, kmTraveled: number): Promise<AttendanceRecord> {
    return api.post<AttendanceRecord>(`/attendance/${recordId}/km`, { kmTraveled });
  },

  async delete(recordId: string): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>(`/attendance/${recordId}`);
  },

  async resetAll(): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>('/attendance/reset');
  },

  async getStats(days?: number | string, staffId?: string, startDate?: string, endDate?: string): Promise<AttendanceStats> {
    const params = new URLSearchParams();
    if (days && days !== 'custom') params.set('days', days.toString());
    if (staffId) params.set('staffId', staffId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    return api.get<AttendanceStats>(`/attendance/stats${qs ? '?' + qs : ''}`);
  },
};

// Daily Report operations
export const dailyReportService = {
  async getAll(): Promise<DailyReport[]> {
    return api.get<DailyReport[]>('/daily-reports');
  },

  async getByStaffId(staffId: string): Promise<DailyReport[]> {
    const reports = await this.getAll();
    return reports.filter(r => r.staffId === staffId);
  },

  async create(report: Omit<DailyReport, 'id' | 'createdAt' | 'updatedAt'>): Promise<DailyReport> {
    return api.post<DailyReport>('/daily-reports', report);
  },

  async update(id: string, content: string): Promise<DailyReport> {
    return api.put<DailyReport>(`/daily-reports/${id}`, { content });
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/daily-reports/${id}`);
  },
};

// Expense operations
export const expenseService = {
  async getAll(params?: { staffId?: string; status?: string; date?: string }): Promise<Expense[]> {
    let endpoint = '/expenses';
    const searchParams = new URLSearchParams();
    if (params?.staffId) searchParams.set('staffId', params.staffId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.date) searchParams.set('date', params.date);
    const queryString = searchParams.toString();
    if (queryString) endpoint += `?${queryString}`;
    return api.get<Expense[]>(endpoint);
  },

  async create(expense: Partial<Expense> & { staffId: string; amount: number }): Promise<Expense> {
    return api.post<Expense>('/expenses', expense);
  },

  async updateStatus(id: string, status: 'approved' | 'rejected', adminRemark?: string): Promise<Expense> {
    return api.put<Expense>(`/expenses/${id}`, { status, adminRemark });
  },

  async update(id: string, data: Partial<Expense>): Promise<Expense> {
    return api.put<Expense>(`/expenses/${id}`, data);
  },

  async getExternalClients(): Promise<string[]> {
    return api.get<string[]>('/external-clients');
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/expenses/${id}`);
  },
};

// Wallet operations
export const walletService = {
  async getBalance(staffId: string): Promise<Wallet> {
    return api.get<Wallet>(`/wallet/${staffId}`);
  },

  async updateBalance(staffId: string, balance: number): Promise<Wallet> {
    return api.put<Wallet>(`/wallet/${staffId}`, { balance });
  },
};

// Paid Leave operations
export const paidLeaveService = {
  async getAll(params?: { staffId?: string; status?: string; date?: string }): Promise<PaidLeave[]> {
    const searchParams = new URLSearchParams();
    if (params?.staffId) searchParams.set('staffId', params.staffId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.date) searchParams.set('date', params.date);
    const qs = searchParams.toString();
    return api.get<PaidLeave[]>(`/paid-leaves${qs ? `?${qs}` : ''}`);
  },

  async create(data: { staffId: string; staffName?: string; date: string; reason?: string }): Promise<PaidLeave> {
    return api.post<PaidLeave>('/paid-leaves', data);
  },

  async updateStatus(id: string, status: 'approved' | 'rejected', adminRemark?: string): Promise<PaidLeave> {
    return api.put<PaidLeave>(`/paid-leaves/${id}`, { status, adminRemark });
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/paid-leaves/${id}`);
  },
};

// Payout operations
export interface Payout {
  id: string;
  staffId: string;
  staffName: string;
  upiId?: string;
  amount: number;
  month: string;
  status: string;
  paymentMode?: string;
  paymentAccount?: string;
  remarks?: string;
  bankReference?: string;
  createdAt: string;
  updatedAt: string;
}

export const payoutService = {
  async getAll(params?: { staffId?: string; month?: string }): Promise<Payout[]> {
    const searchParams = new URLSearchParams();
    if (params?.staffId) searchParams.set('staffId', params.staffId);
    if (params?.month) searchParams.set('month', params.month);
    const qs = searchParams.toString();
    return api.get<Payout[]>(`/payouts${qs ? `?${qs}` : ''}`);
  },

  async create(data: { 
    staffId: string; 
    staffName: string; 
    upiId?: string; 
    amount: number; 
    month: string;
    paymentMode?: string;
    paymentAccount?: string;
    remarks?: string;
    bankReference?: string;
  }): Promise<Payout> {
    return api.post<Payout>('/payouts', data);
  },

  async delete(id: string): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>(`/payouts/${id}`);
  },

  async getSettlements(id: string): Promise<any[]> {
    return api.get<any[]>(`/payouts/${id}/settlements`);
  },
};

// Payout Account operations
export interface PayoutAccount {
  id: string;
  name: string;
  createdAt: string;
}

export const payoutAccountService = {
  async getAll(): Promise<PayoutAccount[]> {
    return api.get<PayoutAccount[]>('/payout-accounts');
  },

  async create(name: string): Promise<PayoutAccount> {
    return api.post<PayoutAccount>('/payout-accounts', { name });
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/payout-accounts/${id}`);
  },
};

// Expense Category operations
export interface ExpenseCategory {
  id: string;
  name: string;
  isSpecial?: number;
  createdAt: string;
}

export const expenseCategoryService = {
  async getAll(): Promise<ExpenseCategory[]> {
    return api.get<ExpenseCategory[]>('/expense-categories');
  },

  async create(name: string, isSpecial: boolean): Promise<ExpenseCategory> {
    return api.post<ExpenseCategory>('/expense-categories', { name, isSpecial });
  },

  async update(id: string, name: string, isSpecial: boolean): Promise<ExpenseCategory> {
    return api.put<ExpenseCategory>(`/expense-categories/${id}`, { name, isSpecial });
  },

  async delete(id: string): Promise<void> {
    return api.delete(`/expense-categories/${id}`);
  },
};

// Auth operations
export const authService = {
  async login(email: string, password: string, role: 'admin' | 'staff'): Promise<Session | null> {
    try {
      const session = await api.post<Session>('/auth/login', { email, password, role });
      localStorage.setItem('royal300_auth_session', JSON.stringify(session));
      return session;
    } catch (e) {
      console.error('Login failed:', e);
      return null;
    }
  },

  logout(): void {
    localStorage.removeItem('royal300_auth_session');
  },

  getSession(): Session | null {
    const data = localStorage.getItem('royal300_auth_session');
    return data ? JSON.parse(data) : null;
  },

  isAuthenticated(): boolean {
    return this.getSession() !== null;
  },
};

// Biometric operations
export const biometricService = {
  async registerFace(staffId: string, faceDescriptor: number[], faceImage: string): Promise<{ success: boolean; staff: Staff }> {
    return api.post(`/staff/${staffId}/register-face`, { faceDescriptor, faceImage });
  },

  async getBiometricStaff(): Promise<Array<{ id: string; name: string; descriptor: number[] }>> {
    return api.get('/staff/biometric');
  },

  async scanAttendance(staffId: string, staffName: string): Promise<{
    type: 'checkin' | 'checkout';
    record: AttendanceRecord;
    message: string;
  }> {
    return api.post('/attendance/scan', { staffId, staffName });
  },

  async deleteFace(staffId: string): Promise<{ success: boolean; staff: Staff }> {
    return api.delete(`/staff/${staffId}/face`);
  },

  /** Fetch today's attendance record for the logged-in staff (returns null if not clocked in yet). */
  async getTodayAttendance(staffId: string): Promise<AttendanceRecord | null> {
    const res = await api.get<{ record: AttendanceRecord | null }>(`/attendance/today/${staffId}`);
    return res.record;
  },

  /** Self-service check-in / check-out: sends the live descriptor + location for server-side verification. */
  async selfScan(payload: {
    staffId: string;
    staffName: string;
    descriptor: Float32Array | number[];
    latitude: number;
    longitude: number;
    kmTraveled?: number;
  }): Promise<{
    type: 'checkin' | 'checkout';
    record: AttendanceRecord;
    message: string;
  }> {
    // Float32Array is not JSON-serialisable — convert to plain number[]
    const descriptor = Array.from(payload.descriptor);
    return api.post('/attendance/self-scan', { ...payload, descriptor });
  },
};

// Settings operations
export const settingsService = {
  async get(key: string): Promise<{ key: string; value: any }> {
    return api.get(`/settings/${key}`);
  },

  async update(key: string, value: any): Promise<{ key: string; value: any }> {
    return api.put(`/settings/${key}`, { value });
  },

  async getOfficeLocation(): Promise<{ latitude: number; longitude: number; radius: number } | null> {
    const setting = await this.get('officeLocation');
    let val = setting.value;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { /* keep as-is */ }
    }
    if (val && val.latitude && val.longitude) {
      return {
        latitude: val.latitude,
        longitude: val.longitude,
        radius: val.radius || 100
      };
    }
    return null;
  },

  async updateOfficeLocation(latitude: number, longitude: number, radius: number = 100): Promise<void> {
    await this.update('officeLocation', { latitude, longitude, radius });
  },

  async getVoiceSettings(): Promise<{ enabled: boolean; successSound: string; failSound: string; clickSound: string; volume: number }> {
    const setting = await this.get('voiceSettings');
    let val = setting.value;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { /* keep as-is */ }
    }
    if (val && typeof val === 'object') {
      return {
        enabled: val.enabled !== false,
        successSound: val.successSound || 'ale-le-le.mp3',
        failSound: val.failSound || 'sabun.mp3',
        clickSound: val.clickSound || 'anime-ahh.mp3',
        volume: val.volume || 0.6,
      };
    }
    return { enabled: true, successSound: 'ale-le-le.mp3', failSound: 'sabun.mp3', clickSound: 'anime-ahh.mp3', volume: 0.6 };
  },

  async updateVoiceSettings(settings: { enabled?: boolean; successSound?: string; failSound?: string; clickSound?: string; volume?: number }): Promise<void> {
    const current = await this.getVoiceSettings();
    await this.update('voiceSettings', { ...current, ...settings });
  },

  async uploadVoiceFile(name: string, file: File): Promise<{ success: boolean; filename: string; path: string }> {
    const formData = new FormData();
    formData.append('audio', file);
    const res = await fetch(`${API_URL}/voice/upload/${name}`, { method: 'POST', body: formData });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  async getVoiceFiles(): Promise<string[]> {
    return api.get<string[]>('/voice/files');
  },
};

export const initializeDefaultAdmin = () => {
  // Deprecated: Admin is handled by backend now.
};
